import PropTypes from 'prop-types';
import { Avatar, Upload } from 'antd';
import './UserAvatarEditModal.css';
import { useMemo, useState } from 'react';
import { filterAcceptedFiles, uploadFile } from '@icp/form-renderer-core';
import { message } from '@icp/settings';
import { useTranslation } from 'react-i18next';
import { Icon } from '@icp/components';
import { ButtonUI, DialogUI } from '../ButtonElement';
import { useDispatch } from '../../store';

const accept = 'image/png, image/jpeg, image/webp';
const size = 128;
const padding = 8;
const borderWidth = 1;
const containerSize = size + (borderWidth + padding) * 2;

function UserAvatarEditModal(props) {
  const { title, shape, value: valueProp, sizeLimit, open, onClose, onChange } = props;

  const { t } = useTranslation(['icp-form-renderer']);
  const [isUploading, setIsUploading] = useState(false);
  const [value, setValue] = useState(null);
  const dispatch = useDispatch();

  const avatarUrl = useMemo(() => {
    const { resources } = value || valueProp || {};
    return resources?.[0]?.url;
  }, [value, valueProp]);

  const doRemove = () => {
    setValue({ resources: [] });
  };

  const doUpload = (params) => {
    setIsUploading(true);
    dispatch(
      uploadFile([params.file], {
        onUploadProgress: ({ total, loaded }) => {
          params.onProgress({ percent: Math.floor((loaded / total) * 100) });
        },
      }),
    )
      .then(
        async (newResources) => {
          setValue({ resources: newResources });
          params.onSuccess(newResources[0]);
        },
        () => {
          message.error(t('error.upload'));
          params.onError();
        },
      )
      .finally(() => {
        setIsUploading(false);
      });
  };

  const customRequest = (params) => {
    const { file } = params;
    const [acceptedFiles] = filterAcceptedFiles([file], accept, sizeLimit);
    if (acceptedFiles.length) {
      doUpload(params);
    }
  };

  const handleOk = async () => {
    if (value) {
      await onChange(value);
    }
    onClose();
  };

  return (
    <DialogUI title={title} open={open} onOk={handleOk} onClose={onClose}>
      <div className="user-avatar-edit-modal">
        <div
          style={{
            border: `${borderWidth}px solid #eee`,
            borderRadius: shape === 'circle' ? '50%' : '6px',
            width: containerSize,
            height: containerSize,
            padding,
          }}
        >
          <Avatar
            alt={t('user-avatar.title')}
            shape={shape}
            size={size}
            icon={<Icon name="ant:user-outlined" size={size * 0.75} />}
            src={avatarUrl}
          />
        </div>
        <div className="user-avatar-edit-modal-actions">
          <div className="user-avatar-edit-modal-upload">
            <Upload
              accept={accept}
              multiple={false}
              maxCount={1}
              customRequest={customRequest}
              showUploadList={false}
            >
              <ButtonUI icon="material:face-rounded" type="primary" loading={isUploading}>
                {t('user-avatar.choose-file')}
              </ButtonUI>
            </Upload>
            <div className="user-avatar-edit-modal-file-limit">
              <div>{t('user-avatar.supported-types', { fileTypes: '*.jpg, *.png, *.webp' })}</div>
              <div>
                {sizeLimit
                  ? t('user-avatar.size-limit', { sizeLimit: `${sizeLimit / 1024.0} KB` })
                  : null}
              </div>
            </div>
          </div>
          <ButtonUI disabled={!avatarUrl} danger={true} onClick={doRemove}>
            {t('user-avatar.remove-avatar')}
          </ButtonUI>
        </div>
      </div>
    </DialogUI>
  );
}

UserAvatarEditModal.propTypes = {
  title: PropTypes.string,
  shape: PropTypes.oneOf(['circle', 'square']),
  value: PropTypes.shape({}),
  sizeLimit: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
};

export default UserAvatarEditModal;
